import { ReactNode, useEffect } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NextPage } from 'next';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { SessionProvider } from 'next-auth/react';
import { GoogleAnalytics } from 'nextjs-google-analytics';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import './styles.css';

import MainLayout from '../components/MainLayout';
import SEO from '../components/SEO';
import { CreditContextProvider } from '../context/credits/provider';
import { GeneratePauseProvider } from '../context/generate/provider';
import { PortalProvider } from '../context/portal/provider';
import { SidebarProvider } from '../context/sidebar/provider';
import { SurveyProvider } from '../context/survey/provider';
import { PageKey } from '../enums/pageKey';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactNode) => ReactNode;
};

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined') {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    person_profiles: 'identified_only',
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com',
    // Enable debug mode in development
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') posthog.debug();
    },
    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    autocapture: false, // Disable automatic event capture, as we capture manually
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).posthog = posthog;
}

const queryClient = new QueryClient();

function CustomApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const getLayout =
    (Component as NextPageWithLayout).getLayout ?? ((page) => page);

  useEffect(() => {
    // Track page views
    const handleRouteChange = () => posthog?.capture('$pageview');
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);

  function ComponentWithLayout() {
    return getLayout(<Component {...pageProps} />);
  }

  return (
    <>
      <SEO pageKey={PageKey.landing} />
      <ToastContainer
        enableMultiContainer
        hideProgressBar
        containerId="universalToastContainer"
      />

      <SessionProvider session={pageProps.session}>
        <PostHogProvider client={posthog}>
          <QueryClientProvider client={queryClient}>
            <GoogleAnalytics trackPageViews />
            <SurveyProvider>
              <CreditContextProvider>
                <PortalProvider>
                  <GeneratePauseProvider>
                    <SidebarProvider>
                      <MainLayout>
                        <ComponentWithLayout />
                      </MainLayout>
                    </SidebarProvider>
                  </GeneratePauseProvider>
                </PortalProvider>
              </CreditContextProvider>
            </SurveyProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </PostHogProvider>
      </SessionProvider>
    </>
  );
}

export default CustomApp;
